import React, { useMemo, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
    FinalWrapper,
    TwoColWrapper,
    Col,
    SingleColTeamCompWrapper,
    FinalLinescoreWrapper,
    FinalCompWrapper,
    SideViewCard
} from './Final.style';
import { withTheme } from 'styled-components';
import TeamSummaryMatchupDataContainer from '../TeamSummaryMatchup/TeamSummaryMatchupDataContainer';
import Skeleton from '@mlb/react-patterns/components/Skeleton/skeleton';
import WrapDataContainer from '../Wrap/containers/WrapDataContainer';
import Tabs from '../Tabs/Tabs';
import BoxscoreDataContainer from '../Boxscore/BoxscoreDataContainer';
import {
    WRAP,
    BOX,
    SUMMARY,
    VIDEO
} from './FinalViews';
import { Overlay, AtBatDataContainer } from '@gameday/components/';
import { ModalTypes } from '@gameday/components/AtBat/atbat.style';
import {
    ATBAT_MODAL_BREAKPOINT,
    GDBREAKPOINTS_FINAL
} from '@gameday/components/themes/breakpoints';
import { useMedia } from '@mlb/react-patterns/utils/hooks/useMedia/useMedia';
import useTracking from '@gameday/utils/hooks/useTracking';
import Card from '../Card/Card';
import SummaryDataContainer from '../Summary/containers/SummaryDataContainer';
import LineScoreDecisions from './final/LineScoreDecisions';
import { useTranslate } from '@gameday/utils/hooks/useTranslate';
import { finalViewsTypes } from '../themes/finalViewsTypes';
import { HighlightsPlaylistDataContainer } from '../HighlightsPlaylist/containers/HighlightsPlaylistDataContainer';
import usePortalController from './usePortalController';

const {
    SM_MAX, MD_MIN, MD_MAX, LG_MIN, EXPAND_SCORES_PANEL
} = GDBREAKPOINTS_FINAL;

const { SINGLENARROW, SINGLEWIDE, TWOCOLS } = finalViewsTypes;

const tabTranslator = function (item, index) {
    const { key, text, id, viewMap } = item;
    const translatedText = this(key, text);

    return {
        text,
        name: translatedText,
        id,
        viewMap
    };
};

const extractViewMap = (tab) => tab.viewMap;

const tabItems = [{
    text: 'Wrap',
    id: 'wrap',
    viewMap: WRAP,
    key: 'button_wrap'
}, {
    text: 'Summary',
    id: 'summary',
    viewMap: SUMMARY,
    key: 'label_summary'
}, {
    text: 'Box',
    id: 'box',
    viewMap: BOX,
    key: 'button_box'
}, {
    text: 'Video',
    id: 'video',
    viewMap: VIDEO,
    key: 'button_video'
}
];

const tabItemsTwoCol = [{
    text: 'Wrap',
    id: 'wrap',
    viewMap: WRAP,
    key: 'button_wrap'
}, {
    text: 'Box',
    id: 'box',
    viewMap: BOX,
    key: 'button_box'
}, {
    text: 'Video',
    id: 'video',
    viewMap: VIDEO,
    key: 'button_video'
}
];
const trackingConfig = ['Final', 'TeamSummaryMatchup'];
const Final = ({
    activeTab,
    handleTabChange,
    viewChangeHandler,
    isLoading,
    numberOfPlays = 0,
    showAtBatOverlay = true,
    game_tab,
    retrievedAtBatPlayIndex,
    gameContext,
    enableTabsFinal,
    videoTabAutoPlay,
    videoTabMute
}) => {
    const displayAtBat = typeof retrievedAtBatPlayIndex !== 'undefined' && retrievedAtBatPlayIndex < numberOfPlays && retrievedAtBatPlayIndex >= 0;

    const translate = useTranslate();
    const Tracking = useTracking();

    const [isOpen, setOpen] = useState(false);
    const [atBatContent, setAtBatContent] = useState(null);
    const [selectedAtBatIndex, setSelectedAtBatIndex] = useState(displayAtBat ? retrievedAtBatPlayIndex : undefined);

    /*
    * LAYOUT TYPES
    * * singleColumnNarrow (mobile): <768px,
    * * singleColumnWide (tablet): 768px- 1439px
    * * twoColumn(desktop): >1440
    */
    const isSingleColumnNarrow = useMedia({ maxWidth: SM_MAX });
    const isSingleColumnWide = useMedia({ minWidth: MD_MIN, maxWidth: MD_MAX });
    const isTwoColumn = useMedia({ minWidth: LG_MIN });
    const twoColumnExpandView = useMedia({ minWidth: EXPAND_SCORES_PANEL });
    const isSingleColOverlay = useMedia({ maxWidth: ATBAT_MODAL_BREAKPOINT });
    const expandView = isSingleColumnWide || twoColumnExpandView;

    let viewType = SINGLEWIDE;
    if (isSingleColumnNarrow) {
        viewType = SINGLENARROW;
    } else if (isSingleColumnWide) {
        viewType = SINGLEWIDE;
    } else if (isTwoColumn) {
        viewType = TWOCOLS;
    }

    const tabs = useMemo(() => {
        return (viewType === TWOCOLS ? tabItemsTwoCol : tabItems)
            .filter((tab) => enableTabsFinal?.[tab.id] !== false)
            .map(tabTranslator, translate);
    }, [tabItems, tabItemsTwoCol, viewType, translate, gameContext, enableTabsFinal]);

    // This whole process is quite a bit different than what we do live.
    // We need to rethink this a bit, and decide on a proper single solution for storing/retrieving
    // the active tab from a set.
    const viewMap = useMemo(() => {
        return tabs.map(extractViewMap);
    }, [tabs]);

    const openAtBatOverlay = useCallback((e) => {
        const playIndex = e?.currentTarget?.dataset?.atbatindex;
        setSelectedAtBatIndex(playIndex);
        Tracking.track.track_atbat_detail_pageload({ playIndex });
    }, [setSelectedAtBatIndex]);

    const onClose = useCallback(() => {
        setOpen(false);
        setSelectedAtBatIndex(undefined);
        const cfg = {
            componentName: ['AtBat', 'Play Details'],
            text: 'Close',
            activity: 'Close Button Click'
        };

        Tracking.track.track_click(cfg);
    }, [setOpen, setSelectedAtBatIndex]);

    const handleTabSwitch = useCallback((event) => {
        const tabIndex = parseInt(event.currentTarget.dataset.index);
        const nextView = viewMap[tabIndex] || (activeTab || WRAP);
        handleTabChange(nextView, tabIndex);
        if (viewChangeHandler) {
            viewChangeHandler(nextView);
        }
    }, [viewChangeHandler, handleTabChange, viewMap, activeTab]);

    useEffect(() => {
        const indexDefined = typeof selectedAtBatIndex !== 'undefined';
        setAtBatContent(indexDefined ? <AtBatDataContainer playIndex={selectedAtBatIndex * 1} /> : null);
        setOpen(indexDefined);
    }, [selectedAtBatIndex]);

    useEffect(() => {
        const firstTab = viewMap[0];
        const activeTabOverride = (game_tab !== '') ? game_tab?.toUpperCase() : undefined;
        const tabIndex = viewMap.indexOf(activeTabOverride);
        const isTabInView = tabIndex > -1;
        if (isTabInView) {
            handleTabChange(activeTabOverride);
            if (viewChangeHandler) {
                viewChangeHandler(activeTabOverride);
            }
        } else {
            if (activeTab === activeTabOverride) {
                handleTabChange(firstTab);
                if (viewChangeHandler) {
                    viewChangeHandler(firstTab);
                }
            }
        }
    }, [viewType, game_tab, viewMap, activeTab]);

    const renderedTabs = useMemo(() => {
        return isLoading
            ? <Skeleton width='343px' height='28px' />
            : <Tabs activeTab={viewMap.indexOf(activeTab)} items={tabs} handleTabChange={handleTabSwitch} />;
    }, [tabs, activeTab, handleTabSwitch, isLoading, viewMap]);

    const teamSummaryMatchup = useMemo(() => {
        return (
            <>
                <TeamSummaryMatchupDataContainer
                    displayRecords={true}
                    displayStandings={expandView}
                    logoSize={expandView ? '48' : '32'}
                    scoreSize={expandView ? '40px' : '32px'}
                    useTri={!expandView}
                    displayGameStateSubtitle={true}
                    componentName={trackingConfig}
                />
                <FinalLinescoreWrapper $expanded={expandView} $singleColumnExpandView={isSingleColumnWide} $twoColumnExpandView={twoColumnExpandView} >
                    <LineScoreDecisions stacked={ !expandView } sidePadding={ expandView ? '' : '16px' } useFullName={twoColumnExpandView}/>
                </FinalLinescoreWrapper>

            </>
        );
    }, [expandView, isSingleColumnWide, twoColumnExpandView, isSingleColumnNarrow]);

    const {
        renderedMainView,
        renderedPortals,
        summary
    } = usePortalController(
        activeTab,
        {
            wrap: <WrapDataContainer viewType={viewType} openAtBatOverlay={openAtBatOverlay} />,
            box: (
                <FinalCompWrapper $viewType={viewType} $paddingOverride={expandView ? '8px 0px 0px' : '0px'} $maxHeightOverride={'100%'}>
                    <BoxscoreDataContainer viewType={expandView ? 'wide' : 'narrow'} showBench={false} showBullpen={false} />
                </FinalCompWrapper>
            ),
            summary: (
                <SideViewCard $viewType={viewType}>
                    <SummaryDataContainer isFinal openAtBatOverlay={openAtBatOverlay} />
                </SideViewCard>
            ),
            video: (
                <FinalCompWrapper $viewType={viewType} $maxHeightOverride={'100%'} $paddingOverride={'0px'}>
                    <HighlightsPlaylistDataContainer showVideoTitle={isTwoColumn || isSingleColumnWide} autoPlay={videoTabAutoPlay} mute={videoTabMute} />
                </FinalCompWrapper>
            )
        },
        {
            isTwoColumn
        }
    );

    return (
        <FinalWrapper
            $viewType={viewType}
            $expanded={expandView}
        >
            {renderedPortals}
            {isTwoColumn
                ? (
                    <TwoColWrapper>
                        <Col>
                            <Card paddingOverride={'0px 16px 16px'}>
                                {teamSummaryMatchup}
                            </Card>
                            {renderedTabs}
                            {renderedMainView}
                        </Col>
                        <Col>
                            {summary}
                        </Col>
                    </TwoColWrapper>
                )
                : (
                    <>
                        <SingleColTeamCompWrapper>
                            {teamSummaryMatchup}
                        </SingleColTeamCompWrapper>
                        {renderedTabs}
                        {renderedMainView}
                    </>
                )
            }
            {showAtBatOverlay && (
                <Overlay
                    isCentered
                    isOpen={isOpen}
                    onClose={onClose}
                    type={isSingleColOverlay ? ModalTypes.ATBAT_VERTICAL : ModalTypes.ATBAT}
                >
                    {atBatContent}
                </Overlay>
            )}
        </FinalWrapper>
    );
};

Final.propTypes = {
    viewChangeHandler: PropTypes.func,
    activeTab: PropTypes.string,
    handleTabChange: PropTypes.func,
    isLoading: PropTypes.bool,
    numberOfPlays: PropTypes.number,
    showAtBatOverlay: PropTypes.bool,
    game_tab: PropTypes.string,
    retrievedAtBatPlayIndex: PropTypes.number,
    enableTabsFinal: PropTypes.object,
    videoTabAutoPlay: PropTypes.bool,
    videoTabMute: PropTypes.bool,
    gameContext: PropTypes.string
};

export default withTheme(Final);
